/**
 * @file DividerFields.tsx
 * @description Линия элемента — толщина и цвет (phase19.16, партия SB120,
 * этап 3б; uiux-sb120-stage3 §3 «Вопросы и ответы», «Таблица», §8; данные
 * db-design-sb120-stage3 §1 `faq.divider`, `table.lines`): толщина 0–4 px
 * (0 — без линии), «↺» — как было (1 px); цвет — `ColorRefField` «Авто:
 * линия» (нет `color` — `--sec-line`), как линия шапки раздела.
 *
 * Отправляется объект линии целиком `{width, color?}` (ширина обязательна);
 * при толщине 0 свой цвет снимается — линии нет, цвету не к чему;
 * «как было» без своего цвета — ключ снимается (`lineValue`), чтобы
 * открыть-сохранить не дописывало `{width: 1}`.
 */

import { DEFAULT_SITE_THEME } from "@chaiberi/shared";

import { ColorRefField } from "../../../site-fields/ColorRefField";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { useSectionForm } from "../section-form-context";
import { lineValue, type ElementLine } from "../../../../lib/site-builder/element-line";

interface Props {
  /** «Разделитель», «Линии». */
  label: string;
  /** «Цвет разделителя», «Цвет линий». */
  colorLabel: string;
  value: ElementLine | undefined;
  onChange: (line: ElementLine | undefined) => void;
  range: { readonly min: number; readonly max: number; readonly default: number };
  /** Полный путь ключа линии: `…elements.2.divider` — предпросмотр `….width`. */
  path: string;
  /** Подпись под толщиной: «Под шапкой — 2 px». */
  hint?: string;
}

export function DividerFields({ label, colorLabel, value, onChange, range, path, hint }: Props) {
  const { theme, preview } = useSectionForm();
  const colors = (theme ?? DEFAULT_SITE_THEME).colors;
  const width = value?.width ?? range.default;
  // Толщина 0 — без линии: свой цвет не храним.
  const lineOf = (w: number) => lineValue(w, w === 0 ? undefined : value?.color, range.default);
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <NumberSliderField
        label={label}
        integer
        value={value?.width}
        inherit={{ value: range.default, label: `как было · ${range.default} px` }}
        min={range.min}
        max={range.max}
        step={1}
        unit="px"
        presets={[
          { label: "Нет 0", value: 0 },
          { label: "1", value: 1 },
          { label: "2", value: 2 },
        ]}
        hint={hint}
        onChange={(w) => onChange(lineOf(w ?? range.default))}
        onPreview={(v) => preview(path, lineOf(v ?? range.default))}
      />
      {width > 0 ? (
        <ColorRefField
          label={colorLabel}
          value={value?.color}
          onChange={(color) => onChange(lineValue(width, color, range.default))}
          colors={colors}
          autoLabel="Авто: линия"
        />
      ) : null}
    </div>
  );
}
